import { useState } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { Download } from 'lucide-react';
import { downloadTrackingExportCSV } from '../utils/parxlCsvExport';
import { useOMS } from '../../../../apps/hooks/oms/useOMS';

interface ParxlTemplateModalProps {
  readonly show: boolean;
  readonly onHide: () => void;
}

function getTodayString(): string {
  const today = new Date();
  return today.toISOString().split('T')[0] ?? '';
}

export function ParxlTemplateModal({ show, onHide }: ParxlTemplateModalProps) {
  const today = getTodayString();
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [isLoading, setIsLoading] = useState(false);

  const oms = useOMS();

  const handleDownload = async () => {
    if (!startDate || !endDate) return;

    setIsLoading(true);
    try {
      const orders = await oms.listOrdersByDateRange(startDate, endDate);
      if (orders.length > 0) {
        downloadTrackingExportCSV(orders, startDate, endDate);
      }
      onHide();
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleShow = () => {
    setStartDate(today);
    setEndDate(today);
  };

  return (
    <Modal show={show} onHide={onHide} onShow={handleShow} centered>
      <Modal.Header closeButton>
        <Modal.Title>Export Orders</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <div className="d-flex gap-3 mb-3">
            <Form.Group className="flex-1">
              <Form.Label>Start Date</Form.Label>
              <Form.Control
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                disabled={isLoading}
              />
            </Form.Group>
            <Form.Group className="flex-1">
              <Form.Label>End Date</Form.Label>
              <Form.Control
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                disabled={isLoading}
              />
            </Form.Group>
          </div>
          <div className="p-3 bg-light rounded">
            <p className="mb-0 small text-muted">
              Export columns: Order ID, Country Code, Tracking Number, Order Date, Order Status, Provider, PIC
            </p>
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleDownload}
          disabled={isLoading || !startDate || !endDate}
          className="d-flex align-items-center gap-2"
        >
          {isLoading ? (
            <>
              <Spinner size="sm" />
              Exporting...
            </>
          ) : (
            <>
              <Download size={16} />
              Download CSV
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

